'use client';

import React, { useContext, useEffect, useState } from 'react';

import { useSearchParams } from 'next/navigation';

import { YouMightAlsoLike } from '../Modules/YouMightAlsoLike';
import { YouMightAlsoLikeVariant } from '../Modules/YouMightAlsoLikeVariant';

import styles from './BespokePLACondensedLayout.module.css';

import { CondensedProductCard } from '@/app/[locale]/products/[slug]/components/CondensedProductCard';
import { RefererContext } from '@/modules/activityTracker/RefererProvider';
import {
  VARIANT_A_IDENTIFIER,
  VARIANT_B_IDENTIFIER,
  VARIANT_IDENTIFIER,
} from '@/modules/experiments/config';
import { useExperiments } from '@/modules/experiments/useExperiments';
import { isExternalPLALander } from '@/modules/product/helpers/isExternalPLALander';
import { useCountryHeader } from '@/modules/user/useCountryHeader';

type Props = {
  children: React.ReactNode;
  similarItemsSash: React.ReactNode;
  estimatedDispatchTime: React.ReactNode;
  nudges: React.ReactNode;
  productBadges: React.ReactNode;
  breadcrumbs: React.ReactNode;
};

export function BespokePLACondensedLayout({
  children,
  similarItemsSash,
  estimatedDispatchTime,
  nudges,
  productBadges,
  breadcrumbs,
}: Props) {
  const params = useSearchParams();
  const referer = useContext(RefererContext) || null;
  const isPLAUser = isExternalPLALander({ params, referer });
  const countryCode = useCountryHeader();
  const isAUS = countryCode?.toUpperCase() === 'AU';

  const [{ web_8744_bespoke_pla_pdp_condensed_v2 }, bucket] = useExperiments(
    ['web_8744_bespoke_pla_pdp_condensed_v2'],
    { deferred: true }
  );

  const [{ web_9027_filterable_ymal_on_condensed_pla }, bucketFilterableYmal] =
    useExperiments(['web_9027_filterable_ymal_on_condensed_pla'], {
      deferred: true,
    });

  const [, bucketBespokeCondensedPDP] = useExperiments(
    ['web_9153_bespoke_condensed_pdp'],
    { deferred: true }
  );

  const [showFullPDP, setShowFullPDP] = useState(false);

  const isCondensedPLAVariantA =
    web_8744_bespoke_pla_pdp_condensed_v2 === VARIANT_A_IDENTIFIER;
  const isCondensedPLAVariantB =
    web_8744_bespoke_pla_pdp_condensed_v2 === VARIANT_B_IDENTIFIER;
  const isInVariant = isCondensedPLAVariantA || isCondensedPLAVariantB;

  useEffect(() => {
    if (isPLAUser && !isAUS) {
      bucket();
    }
  }, [isPLAUser, isAUS, bucket]);

  /* Only bucket users who can actually be exposed to the filterable YMAL,
  i.e. those already in the condensed PLA variant B experience */
  useEffect(() => {
    if (isPLAUser && !isAUS && isCondensedPLAVariantB) {
      bucketFilterableYmal();
    }
  }, [isPLAUser, isAUS, isCondensedPLAVariantB, bucketFilterableYmal]);

  const isCondensedEligible = isPLAUser && !isAUS && isInVariant;
  const showCondensed = isCondensedEligible && !showFullPDP;

  useEffect(() => {
    if (isCondensedEligible) {
      bucketBespokeCondensedPDP();
    }
  }, [isCondensedEligible, bucketBespokeCondensedPDP]);

  const isFilterableYmalVariant =
    web_9027_filterable_ymal_on_condensed_pla === VARIANT_IDENTIFIER &&
    isCondensedPLAVariantB;

  return (
    <>
      {showCondensed && (
        <>
          <div className={styles.breadcrumbsWrapper}>{breadcrumbs}</div>
          {similarItemsSash}

          <CondensedProductCard
            onViewDetailsClickAction={() => setShowFullPDP(true)}
            estimatedDispatchTime={estimatedDispatchTime}
            nudges={nudges}
            productBadges={productBadges}
            isCondensedPLAVariantB={isCondensedPLAVariantB}
            isCondensedPLAVariantA={isCondensedPLAVariantA}
          />

          {isFilterableYmalVariant ? (
            <YouMightAlsoLikeVariant />
          ) : (
            <YouMightAlsoLike />
          )}
        </>
      )}
      {!showCondensed && (
        <>
          {similarItemsSash}
          {children}
        </>
      )}
    </>
  );
}
